import { useState, useCallback } from 'react';
import {
  Product,
  ProductResponse,
  SearchProductsRequest,
  ACLAuthorizationRequest,
  SearchProductsPostContentTypeEnum,
  CreateProductContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getProductsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseProductsReturn {
  products: ProductResponse[];
  loading: boolean;
  searchError: string | null;
  selectedProduct: ProductResponse | null;
  setSelectedProduct: (product: ProductResponse | null) => void;
  fetchProducts: (page?: number, pageSize?: number, searchBody?: SearchProductsRequest) => Promise<boolean>;
  createProduct: (productData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useProducts = (): UseProductsReturn => {
  const [items, setItems] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchProducts = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchProductsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getProductsApi();
      const response = await api.searchProductsPost({
        contentType: SearchProductsPostContentTypeEnum.ApplicationJson,
        searchProductsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch products');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createProduct = useCallback(async (productData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getProductsApi();

      const createdProduct = await api.createProduct({
        contentType: CreateProductContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        product: productData as Product,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdProductId = createdProduct?.id;

      showSuccess(
        'Product created successfully',
        createdProductId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('product', createdProductId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create product', 'Failed to create product');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteProduct = useCallback(async (productId: string) => {
    try {
      const api = getProductsApi();
      await api.deleteProductById({ productId });
      showSuccess('Product deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete product', 'Failed to delete product');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    products: items,
    loading,
    searchError,
    selectedProduct,
    setSelectedProduct,
    fetchProducts,
    createProduct,
    deleteProduct,
    setTotal,
    total,
  };
};
